import { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Fa,
  showErrorMessage,
  showSuccessMessage,
  TextInput,
  Toggle,
  triggerPrompt,
} from '@eltoro-ui/components'
import { v4 as uuid } from 'uuid'
import classNames from 'classnames'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  V1Subscription,
  V1Pixel,
  V1Publisher,
  V1PublisherSubscription,
  V1Rule,
  V1RuleSet,
  V1SubscriptionNote,
  V1WebToHomeNoteType,
} from 'next-gen-sdk'
import dayjs from 'dayjs'
import { useAuth } from 'react-oidc-context'
import DatePicker from 'react-datepicker'
import { useAppContext } from 'Contexts'
import { formatDate, isValidRegex } from 'Helpers'
import {
  IncludedTimeInput,
  includedTimeOptions,
  PixelFormItem,
  PixelNotes,
  PixelSubscriptionHistory,
  PixelTagInput,
  SupportRequestModal,
} from 'Components'
import { updateRuleSet } from 'Requests/updateRuleSet'
import { createRuleSet } from 'Requests/createRuleSet'
import { hasRuleSetChanged } from '../../Utils/ruleSetComparison'
import { listOrgSubscriptions } from '../../Requests'
import { WebToHomeDisabled } from 'Pages/PixelLibraryPage'
import { Loading, RuleSetWithTempId, RuleSets } from 'Components'
import { PixelGeoLocationFilters } from '../../Components/Pixel/GeoLocationFilters'
import { SubscriptionMetricsCard } from './components/SubscriptionMetricsCard'
import './SubscriptionPage.scss'

//TODO: Use Create Subscription endpoint to update includedTime???
export const PixelSubscriptionsPage = () => {
  const [subscription, setSubscription] = useState<Partial<V1Subscription>>()
  const [pixel, setPixel] = useState<V1Pixel>()
  const [showMatchCap, setShowMatchCap] = useState(false)
  const [publishers, setPublishers] = useState<V1Publisher[]>([])
  const {
    webToHomeApi,
    webToHomePrintPartnerApi,
    currentOrg,
    isAdmin,
    isReadOnly,
    webToHomeEnabled,
    searchServiceApi,
    groups,
  } = useAppContext()

  // form pieces
  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [parentRuleSet, setParentRuleSet] = useState<
    Partial<RuleSetWithTempId>
  >({
    operator: 'and',
    rules: [{ term: '', tempId: uuid(), type: 'term' }],
    tempId: uuid(),
  })
  // Store original rule set for change detection
  const [originalParentRuleSet, setOriginalParentRuleSet] = useState<
    Partial<RuleSetWithTempId>
  >()
  const [trialPeriodDates, setTrialPeriodDates] = useState({
    start: dayjs().toDate(),
    end: dayjs().add(2, 'week').toDate(),
  })
  const [selectedPublisher, setSelectedPublisher] = useState<V1Publisher>()
  const [matchCap, setMatchCap] = useState(0)
  const [locked, setLocked] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [autoEnableAfterTrial, setAutoEnableAfterTrial] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [showNoteAdd, setShowNoteAdd] = useState(false)
  const [notes, setNotes] = useState<{
    content: string
    type: V1WebToHomeNoteType
  }>()
  const [saving, setSaving] = useState(false)
  const [ftpPath, setFtpPath] = useState('')
  const [sendToFileServer, setSendToFileServer] = useState(false)
  const [excludeDuplicates, setExcludeDuplicates] = useState(false)
  const [excludedTime, setExcludedTime] = useState<number>()
  const [supportModal, setSupportModal] = useState(false)
  const [noteEditing, setNoteEditing] = useState<V1SubscriptionNote>()
  const [retargetingEnabled, setRetargetingEnabled] = useState(false)
  const [includedTime, setIncludedTime] = useState<number>(15552000)

  const auth = useAuth()
  const user = auth.user?.profile

  let { pixelId, subscriptionId, publisherId } = useParams<{
    pixelId?: string
    subscriptionId: string
    publisherId?: string
  }>()
  const isPartner = !!publisherId
  const nav = useNavigate()
  const query = currentOrg?.id ? `?org_id=${currentOrg.id}` : ''

  // list publishers dropdown
  const assignTempIds = (ruleSet: RuleSetWithTempId): RuleSetWithTempId => {
    return {
      ...ruleSet,
      tempId: uuid(),
      rules: ruleSet.rules?.map((rule) => ({
        ...rule,
        tempId: uuid(),
      })),
      ruleSet: ruleSet.ruleSet?.map((rs) => assignTempIds(rs)),
    }
  }

  const excludeTimeOptions = [
    { value: 86400, label: '24 Hours' },
    { value: 172800, label: '48 Hours' },
    { value: 259200, label: '3 Days' },
    { value: 345600, label: '4 Days' },
    { value: 604800, label: '7 Days' },
    { value: 1209600, label: '14 Days' },
    { value: 1209600, label: '2 Weeks' },
    { value: 2419200, label: '4 Weeks' },
    { value: 4838400, label: '8 Weeks' },
  ]

  useEffect(() => {
    if (!webToHomeApi || !currentOrg?.id) return

    const handleFetchPublishers = async () => {
      let pub: V1Publisher[] = []
      await webToHomeApi
        .advertisingPlatformServiceListPublishers(
          1000, // pageSize
          undefined, // pageToken
          'name', // orderBy
        )
        .then((res) => {
          pub = [...pub, ...(res.publishers || [])]
        })
      setPublishers(pub)
      // TODO: ask targeting team about the pagination on this endpoint, it seems to always return the same nextPageToken
      // so this becomes an infinite loop and crashes the page
      // for now i think 1000 is enough to get all publishers on prod
      // let nextPageToken = ''
      // let pub: V1Publisher[] = []
      // do {
      //   await webToHomeApi
      //     .advertisingPlatformServiceListPublishers(
      //       100, // pageSize
      //       nextPageToken, // pageToken
      //       'name', // orderBy
      //     )
      //     .then((res) => {
      //       pub = [...pub, ...(res.publishers || [])]
      //       nextPageToken = res.nextPageToken || ''
      //     })
      //   setPublishers(pub)
      // } while (nextPageToken !== '')
    }

    if (isAdmin)
      handleFetchPublishers().catch((err) => {
        showErrorMessage('Error fetching publishers', '')
      })
  }, [currentOrg?.id, webToHomeApi])

  // fetch subscription
  useEffect(() => {
    const handleGetSubscriptionFromPartnerOnly = async (
      sub: V1PublisherSubscription,
    ) => {
      if (!sub?.orgName || !sub?.id) return
      let orgId = ''
      const searchResults = await searchServiceApi?.advertisingPlatformServiceGlobalSearch(
        undefined,
        [sub?.orgName],
      )
      orgId = searchResults?.orgs?.[0]?.orgId || ''
      if (!orgId) return
      const listResults = await listOrgSubscriptions(
        1, // pageSize
        undefined, // pageToken
        orgId, // "orgId"
        undefined, // orderBy
        subscriptionId, // subscriptionId
      )
      const fullSubscription = listResults?.subscriptions?.[0]
      if (fullSubscription) {
        setSubscription((prev) => ({ ...prev, ...fullSubscription }))
        setEnabled(fullSubscription?.enabled || false)
        setMatchCap(
          fullSubscription?.matchCap && fullSubscription.matchCap > 0
            ? fullSubscription.matchCap
            : 0,
        )
        if (fullSubscription?.matchCap && fullSubscription.matchCap > 0)
          setShowMatchCap(true)
        setLocked(fullSubscription?.locked || false)
        setHidden(fullSubscription?.hidden || false)
        setAutoEnableAfterTrial(fullSubscription?.autoEnableAfterTrial || false)
        setTrialPeriodDates({
          start: new Date(fullSubscription?.trialPeriodStartDate || new Date()),
          end: new Date(fullSubscription?.trialPeriodEndDate || new Date()),
        })
        if (fullSubscription.publisherId)
          setSelectedPublisher({ id: fullSubscription.publisherId })
        if (fullSubscription.tags) setTags(fullSubscription.tags)
        if (fullSubscription.ftpPath) setFtpPath(fullSubscription.ftpPath)
        if (fullSubscription.sendToFileServer)
          setSendToFileServer(fullSubscription.sendToFileServer)
        if (fullSubscription.excludedTime) {
          setExcludedTime(fullSubscription.excludedTime)
          setExcludeDuplicates(true)
        }
        setRetargetingEnabled(fullSubscription.retargetingEnabled || false)
        setIncludedTime(fullSubscription.includedTime || 15552000)
      }
    }
    if (
      !subscriptionId ||
      !currentOrg?.id ||
      !webToHomeApi ||
      (!pixelId && !subscriptionId)
    )
      return

    if (pixelId) {
      webToHomeApi
        ?.advertisingPlatformServiceGetSubscription(
          pixelId,
          subscriptionId,
          currentOrg.id,
        )
        .then((sub) => {
          setSubscription(sub)
          setName(sub?.name || '')
          setEnabled(sub?.enabled || false)
          setMatchCap(sub?.matchCap && sub.matchCap > 0 ? sub.matchCap : 0)
          if (sub?.matchCap && sub.matchCap > 0) setShowMatchCap(true)
          setLocked(sub?.locked || false)
          setHidden(sub?.hidden || false)
          setAutoEnableAfterTrial(sub?.autoEnableAfterTrial || false)
          setTrialPeriodDates({
            start: new Date(sub?.trialPeriodStartDate || new Date()),
            end: new Date(
              sub?.trialPeriodEndDate || dayjs().add(2, 'week').toDate(),
            ),
          })
          if (sub.publisherId) setSelectedPublisher({ id: sub.publisherId })
          if (sub.tags) setTags(sub.tags)
          if (sub.ftpPath) setFtpPath(sub.ftpPath)
          setSendToFileServer(sub.sendToFileServer || false)
          if (sub.excludedTime) {
            setExcludedTime(sub.excludedTime)
            setExcludeDuplicates(true)
          }
          setRetargetingEnabled(sub.retargetingEnabled || false)
          setIncludedTime(sub.includedTime || 15552000)
          return sub
        })
        .then(async (sub) => {
          if (webToHomeApi && pixelId && sub.orgId) {
            const pixel = await webToHomeApi
              ?.advertisingPlatformServiceGetPixel(pixelId, sub.orgId)
              .catch(() => {
                showErrorMessage('Error fetching pixel', '')
                return undefined
              })
            setPixel(pixel)
          }

          if (sub.ruleSet) {
            const resWithTempIds = assignTempIds(sub.ruleSet)
            setParentRuleSet(resWithTempIds)
            // Store original for change detection (without tempIds for accurate comparison)
            setOriginalParentRuleSet(sub.ruleSet)
          }
        })
        .catch(() => {
          showErrorMessage('Error fetching subscription', '')
        })
    }

    if (isPartner && publisherId && webToHomePrintPartnerApi) {
      webToHomePrintPartnerApi
        .advertisingPlatformServiceGetPublisherSubscription(
          publisherId,
          subscriptionId,
          publisherId, // currentOrg.id,
        )
        .then((sub) => {
          setSubscription(sub)
          setName(sub?.name || '')
          setEnabled(sub?.enabled || false)
          if (sub.publisherId) setSelectedPublisher({ id: sub.publisherId })
          if (sub.ftpPath) setFtpPath(sub.ftpPath)
          setSendToFileServer(sub.sendToFileServer || false)

          // if isAdmin, fetch full subscription
          if (isAdmin && sub.orgName && sub.id) {
            handleGetSubscriptionFromPartnerOnly(sub)
          }

          return sub
        })
        .catch(() => {
          showErrorMessage('Error fetching subscription', '')
        })
    }
  }, [currentOrg?.id, pixelId, subscriptionId, webToHomeApi])

  // prevent user from navigating away
  useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', unloadCallback)
    return () => window.removeEventListener('beforeunload', unloadCallback)
  }, [])

  const setters = {
    setParentRuleSet,
    setSubscription,
  }
  const handleNameChange = (e: any) => {
    const { value } = e.target
    if (value) {
      setName(value)
    }
  }

  const handleUpdateSubscription = async () => {
    if (
      !isAdmin &&
      isPartner &&
      publisherId &&
      subscriptionId &&
      webToHomePrintPartnerApi
    ) {
      const updatedPublisherSubscription: V1PublisherSubscription = {
        enabled,
        ftpPath,
      }
      setSaving(true)
      await webToHomePrintPartnerApi
        ?.advertisingPlatformServiceUpdatePublisherSubscription(
          publisherId,
          subscriptionId,
          updatedPublisherSubscription,
          undefined,
          currentOrg!.id!,
        )
        .then(async (res) => {
          setSubscription(res)
          showSuccessMessage('Successfully updated publisher subscription', '')
        })
        .catch(() =>
          showErrorMessage('Error updating publisher subscription', ''),
        )
        .finally(() => setSaving(false))
      return
    }

    if (isPartner && isAdmin) {
      const updatedSubscription = {
        enabled: enabled,
        matchCap: showMatchCap && matchCap > 0 ? matchCap : undefined,
        locked,
        hidden,
        autoEnableAfterTrial,
        trialPeriodStartDate: trialPeriodDates.start,
        trialPeriodEndDate: trialPeriodDates.end,
        publisherId: selectedPublisher?.id,
        ftpPath,
        sendToFileServer: isAdmin ? sendToFileServer : undefined,
        excludedTime: excludedTime || 0, // zero to unset
        retargetingEnabled,
        includedTime,
      }

      if (!subscription?.pixelId || !subscription.id || !subscription.orgId)
        return
      setSaving(true)
      await webToHomeApi
        ?.advertisingPlatformServiceUpdateSubscription(
          subscription?.pixelId,
          subscription.id,
          updatedSubscription,
          undefined,
          subscription.orgId,
        )
        .then(async (res) => {
          setSubscription(res)
          showSuccessMessage('Successfully updated subscription', '')
          if (
            publisherId !== res.publisherId &&
            res.publisherId &&
            res.id &&
            res.orgId
          ) {
            nav(
              `/pixel-library/publisher/${res.publisherId}/subscription/${res.id}?org_id=${res.orgId}`,
            )
          }
        })
        .catch(() => showErrorMessage('Error updating subscription', ''))
        .finally(() => setSaving(false))

      // do not advance to updatedSubscription stage
      return
    }

    const updatedSubscription = {
      name: name,
      enabled: enabled,
      statesFiltered: subscription?.statesFiltered || [],
      zipsFiltered: subscription?.zipsFiltered || [],
      statesIncluded: subscription?.statesIncluded || [],
      zipsIncluded: subscription?.zipsIncluded || [],
      matchCap: showMatchCap && matchCap > 0 ? matchCap : undefined,
      locked,
      hidden,
      autoEnableAfterTrial,
      trialPeriodStartDate: trialPeriodDates.start,
      trialPeriodEndDate: trialPeriodDates.end,
      publisherId: selectedPublisher?.id,
      tags,
      ftpPath: isAdmin ? ftpPath : undefined,
      sendToFileServer: isAdmin ? sendToFileServer : undefined,
      excludedTime: excludedTime || 0, // zero to unset
      retargetingEnabled,
      includedTime,
    }
    setSaving(true)
    if (!subscription?.pixelId || !subscription.id || !subscription.orgId)
      return
    await webToHomeApi
      ?.advertisingPlatformServiceUpdateSubscription(
        subscription.pixelId,
        subscription.id,
        updatedSubscription,
        undefined,
        subscription.orgId,
      )
      .then(async (res) => {
        if (!res.pixelId || !res.orgId || !res.id) return
        let updatedRuleSet: V1RuleSet | undefined

        if (subscription?.ruleSet?.id) {
          // Check if rule set has actually changed before calling updateRuleSet
          const hasChanges = originalParentRuleSet
            ? hasRuleSetChanged(originalParentRuleSet, parentRuleSet)
            : true // If no original, assume changes exist

          if (hasChanges) {
            updatedRuleSet = await updateRuleSet(
              parentRuleSet,
              res.pixelId,
              res.id,
              res.orgId,
              originalParentRuleSet, // Pass original for change detection
            )
          } else {
            // No changes detected, use the original rule set
            updatedRuleSet = subscription.ruleSet
            console.log('No rule set changes detected, skipping update')
          }
        } else {
          updatedRuleSet = await createRuleSet(
            parentRuleSet,
            res.pixelId,
            res.id,
            res.orgId,
          )
        }
        setSubscription({ ...res, ruleSet: updatedRuleSet })
        if (updatedRuleSet) {
          const resWithTempIds = assignTempIds(updatedRuleSet)
          setParentRuleSet(resWithTempIds)
          // Update original rule set for future comparisons
          setOriginalParentRuleSet(updatedRuleSet)
        }
        showSuccessMessage('Successfully updated subscription', '')
      })
      .catch(() => showErrorMessage('Error updating subscription', ''))
      .finally(() => setSaving(false))
  }
  const handleSaveNote = async () => {
    if (
      isPartner &&
      publisherId &&
      subscriptionId &&
      webToHomePrintPartnerApi
    ) {
      setSaving(true)
      webToHomePrintPartnerApi
        .advertisingPlatformServiceCreatePublisherSubscriptionNote(
          publisherId,
          subscriptionId,
          {
            content: notes?.content || '',
            type:
              isAdmin && notes?.type === 'NOTE_TYPE_ADMIN'
                ? 'NOTE_TYPE_ADMIN'
                : 'NOTE_TYPE_PUBLISHER',
          },
          publisherId, // orgId,
        )
        .then((res) => {
          setSubscription((prev) => ({
            ...prev,
            notes: [...(prev?.notes || []), res],
          }))
          setShowNoteAdd(false)
        })
        .catch(() =>
          showErrorMessage('Error saving publisher subscription note', ''),
        )
        .finally(() => setSaving(false))

      return
    }
    if (
      !pixelId ||
      !subscriptionId ||
      !currentOrg?.id ||
      !notes?.content ||
      !webToHomeApi
    )
      return
    setSaving(true)
    webToHomeApi
      .advertisingPlatformServiceCreateSubscriptionNote(
        pixelId,
        subscriptionId,
        {
          content: notes?.content || '',
          type: notes?.type || 'NOTE_TYPE_USER',
        },
        currentOrg!.id!,
      )
      .then((res) => {
        setSubscription((prev) => ({
          ...prev,
          notes: [...(prev?.notes || []), res],
        }))
        setShowNoteAdd(false)
      })
      .catch(() => showErrorMessage('Error saving subscription note', ''))
      .finally(() => setSaving(false))
  }
  const handleUpdateNote = async (newNote: V1SubscriptionNote) => {
    if (
      isPartner &&
      publisherId &&
      subscriptionId &&
      webToHomePrintPartnerApi &&
      newNote.id &&
      subscription?.orgName
    ) {
      setSaving(true)
      webToHomePrintPartnerApi
        .advertisingPlatformServiceUpdatePublisherSubscriptionNote(
          publisherId,
          subscriptionId,
          newNote.id,
          {
            content: newNote.content || '',
            type: newNote.type,
          },
          undefined,
          publisherId, // orgId,
        )
        .then((res) => {
          setSubscription((prev) => ({
            ...prev,
            notes:
              prev?.notes?.map((note) => (note.id === res.id ? res : note)) ||
              [],
          }))
          setNoteEditing(undefined)
        })
        .catch(() =>
          showErrorMessage(
            'Error saving publisher subscription note edits',
            'You may not have permission to edit this note',
          ),
        )
        .finally(() => setSaving(false))

      return
    }
    if (
      !pixelId ||
      !subscriptionId ||
      !currentOrg?.id ||
      !webToHomeApi ||
      !newNote.id
    )
      return
    setSaving(true)
    webToHomeApi
      .advertisingPlatformServiceUpdateSubscriptionNote(
        pixelId,
        subscriptionId,
        newNote.id,
        {
          content: newNote.content || '',
          type: newNote.type,
        },
        undefined,
        subscription?.orgId,
      )
      .then((res) => {
        setSubscription((prev) => ({
          ...prev,
          notes:
            prev?.notes?.map((note) => (note.id === res.id ? res : note)) || [],
        }))
        setNoteEditing(undefined)
      })
      .catch(() => {
        showErrorMessage(
          'Error saving subscription note edits',
          'You may not have permission to edit this note',
        )
      })
      .finally(() => setSaving(false))
  }

  const handleDeleteNote = (noteId: string) => {
    triggerPrompt(
      'Delete note',
      <p>Are you sure you want to delete this note?</p>,
      () => {
        if (
          isPartner &&
          publisherId &&
          subscriptionId &&
          webToHomePrintPartnerApi
        ) {
          setSaving(true)
          webToHomePrintPartnerApi
            .advertisingPlatformServiceDeletePublisherSubscriptionNote(
              publisherId,
              subscriptionId,
              noteId,
              publisherId,
            )
            .then(() => {
              setSubscription((prev) => ({
                ...prev,
                notes: prev?.notes?.filter((note) => note.id !== noteId),
              }))
              setShowNoteAdd(false)
            })
            .catch(() =>
              showErrorMessage('Error deleting subscription note', ''),
            )
            .finally(() => setSaving(false))
        }
        if (!pixelId || !subscriptionId || !currentOrg?.id || !webToHomeApi)
          return
        setSaving(true)

        webToHomeApi
          .advertisingPlatformServiceDeleteSubscriptionNote(
            pixelId,
            subscriptionId,
            noteId,
            currentOrg!.id!,
          )
          .then(() => {
            setSubscription((prev) => ({
              ...prev,
              notes: prev?.notes?.filter((note) => note.id !== noteId),
            }))
            setShowNoteAdd(false)
          })
          .catch(() => showErrorMessage('Error deleting subscription note', ''))
          .finally(() => setSaving(false))
      },
    )
  }

  const handleDeleteRule = async (ruleToDelete: V1Rule) => {
    if (
      !pixelId ||
      !subscriptionId ||
      !currentOrg?.id ||
      !ruleToDelete.id ||
      !ruleToDelete.ruleSetId
    )
      return
    await webToHomeApi
      ?.advertisingPlatformServiceDeleteRule(
        pixelId,
        subscriptionId,
        ruleToDelete.ruleSetId,
        ruleToDelete.id,
        currentOrg.id,
      )
      .catch(() => showErrorMessage('Error deleting rule', ''))
  }

  const handleDeleteRuleSet = async (ruleSetToDelete: V1RuleSet) => {
    if (!pixelId || !subscriptionId || !currentOrg?.id || !ruleSetToDelete.id)
      return
    await webToHomeApi
      ?.advertisingPlatformServiceDeleteRuleSet(
        pixelId,
        subscriptionId,
        ruleSetToDelete.id,
        currentOrg.id,
      )
      .catch(() => showErrorMessage('Error deleting rule set', ''))
  }

  const isValidRuleSet = () => {
    const getAllNestedRules = (ruleSet: RuleSetWithTempId): V1Rule[] => {
      let rules: V1Rule[] = ruleSet.rules || []
      if (ruleSet.ruleSet?.length) {
        ruleSet.ruleSet.forEach((nestedRuleSet) => {
          rules = rules.concat(getAllNestedRules(nestedRuleSet))
        })
      }
      return rules
    }
    const allRules = getAllNestedRules(parentRuleSet)
    const allRegexRules = allRules.filter((rule) => rule.type === 'regex')
    return (
      allRules.length > 0 && // there are some rules
      allRegexRules.every((rule) => rule.term && isValidRegex(rule.term)) // the regex rules are valid
    )
  }
  const handleIncludedTimeChange = (option: number) => {
    setIncludedTime(option)
  }

  if (!isAdmin && !webToHomeEnabled) return <WebToHomeDisabled />

  if (isPartner && !currentOrg?.isPublisher)
    return (
      <div className="bg-danger-300 text-l flex items-center justify-center gap-2 rounded-sm p-4 text-white">
        This organization is not a publisher.
      </div>
    )

  if (!subscription) return <Loading />

  return (
    <div className="PixelSubscriptionPage">
      <div className="PixelSubscriptionContainer">
        <div className="min-h-screen bg-[color:var(--base-gray)]">
          <div className="mx-auto max-w-7xl px-4 py-8 text-[color:var(--base-text)] sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex w-full items-center justify-between">
                  <Link
                    to={`/pixel-library/${
                      isPartner ? 'partner-' : ''
                    }subscriptions${query}`}
                    className="flex items-center"
                  >
                    <Fa icon="arrow-left" size={1} className="mr-2 h-5 w-5" />
                    Back to{isPartner ? ' Partner' : ''} Subscription Library
                  </Link>
                  {isPartner && (
                    <Button onClick={() => setSupportModal(true)}>
                      Support Request
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="flex items-center gap-1 text-2xl font-semibold text-[color:var(--base-text)]">
                    {subscription?.name || 'Subscription Name'}
                    {subscription?.locked && <Fa icon="lock" size={1} />}
                    {subscription?.hidden && <Fa icon="user-secret" size={1} />}
                  </h1>
                  <div className="mt-2 flex items-center text-sm text-[color:var(--color-grey-500)]">
                    {!isPartner && <span className="mr-2">web-to-home</span>}
                    {subscription.retargetingEnabled && (
                      <span className="mr-2">Digital Targeting</span>
                    )}

                    {(() => {
                      if (!subscription) return null
                      const status = (() => {
                        if (subscription.enabled && !subscription.publisherId)
                          return 'Ready'
                        if (subscription.enabled && subscription.publisherId)
                          return 'Active'
                        return 'Created'
                      })()
                      return (
                        <div
                          className={classNames(
                            'max-w-min rounded-full py-1 px-3 font-bold',
                            {
                              'text-base-info bg-base-info-50':
                                status === 'Ready',
                              'text-success-500 bg-success-50':
                                status === 'Active',
                              'bg-warning-50 text-warning-500':
                                status === 'Created',
                            },
                          )}
                        >
                          {status}
                        </div>
                      )
                    })()}
                  </div>
                  <div className="mt-1 text-sm text-[color:var(--color-grey-500)]">
                    Created on:{' '}
                    {subscription?.created
                      ? formatDate(subscription?.created)
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics Card */}
            <SubscriptionMetricsCard
              subscription={subscription}
              isPartner={isPartner}
            />

            {/* Subscription Name */}
            {isPartner ? (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow-lg">
                <div className="flex flex-col gap-2 p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Basic Information
                  </h2>
                  <PixelFormItem
                    htmlFor="subscription-name"
                    label="Subscription Name"
                  >
                    <input
                      type="text"
                      value={name}
                      className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-2 text-[color:var(--base-text)]"
                      onChange={(e) => handleNameChange(e)}
                      disabled
                    />
                  </PixelFormItem>
                  <PixelFormItem htmlFor="pixel-name" label="Pixel Name">
                    <input
                      type="text"
                      value={
                        (subscription as V1PublisherSubscription).pixelName ||
                        ''
                      }
                      className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-2 text-[color:var(--base-text)]"
                      onChange={(e) => handleNameChange(e)}
                      disabled
                    />
                  </PixelFormItem>
                </div>
              </div>
            ) : (
              <div className="mb-6 flex rounded-lg bg-[color:var(--base-bg)] shadow-lg">
                <div className="w-full p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Subscription Name
                  </h2>
                  <input
                    type="text"
                    value={name}
                    className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-2 text-[color:var(--base-text)]"
                    onChange={(e) => handleNameChange(e)}
                    disabled={isReadOnly || subscription?.locked}
                  />
                </div>
                <div className="w-full p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Pixel Name
                  </h2>
                  <div className="flex items-center justify-between gap-2">
                    <input
                      type="text"
                      value={pixel?.name || 'N/A'}
                      className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-2 text-[color:var(--base-text)]"
                      onChange={(e) => handleNameChange(e)}
                      disabled
                    />
                    <Button
                      iconOnly={
                        <Fa icon="arrow-up-right-from-square" size={1} />
                      }
                      className="!border-none"
                      onClick={() =>
                        nav(`/pixel-library/pixel/${pixelId}${query}`)
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow-lg">
                <div className="p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Digital Targeting
                  </h2>
                  <div className="mb-4 flex items-center">
                    <Toggle
                      isChecked={retargetingEnabled}
                      onChange={(checked) => setRetargetingEnabled(checked)}
                      size="m"
                      label="Enable Digital Targeting"
                      isDisabled={isReadOnly || subscription?.locked}
                    />
                  </div>

                  {retargetingEnabled && (
                    <>
                      <h2 className="text-md mb-2 font-semibold text-[color:var(--base-text)]">
                        Keep IP In Segment
                      </h2>
                      <IncludedTimeInput
                        onChange={(option) =>
                          handleIncludedTimeChange(Number(option.value))
                        }
                        defaultValue={includedTimeOptions.find(
                          (option) => option.value === String(includedTime),
                        )}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Tags */}
            {!isPartner && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow-lg">
                <div className="p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Tags
                  </h2>
                  <PixelTagInput
                    tags={tags}
                    setTags={setTags}
                    disabled={isReadOnly || subscription?.locked}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow">
              <div className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[color:var(--base-text)]">
                    Notes
                  </h2>
                  <Button
                    kind="text"
                    onClick={() => setShowNoteAdd(!showNoteAdd)}
                  >
                    {showNoteAdd ? 'Close' : 'Add Note'}
                  </Button>
                </div>
                {showNoteAdd && (
                  <div className="animate-slidedown mb-2 flex flex-col py-1">
                    <PixelNotes
                      type="subscription"
                      onChange={(content, type) => setNotes({ content, type })}
                      showLabel={false}
                      onSaveNote={handleSaveNote}
                      saving={saving}
                    />
                  </div>
                )}
                <div className="PixelSubscriptionPage__notes-table mt-2 flex flex-col gap-2">
                  {(subscription?.notes || []).length > 0 ? (
                    subscription?.notes?.map((currentNote) => {
                      const {
                        content,
                        type,
                        userName,
                        updateTime,
                        id,
                        userId,
                        createTime,
                      } = currentNote
                      const canEditNote =
                        !isReadOnly && (userId === user?.sub || isAdmin)
                      if (!isAdmin && type === 'NOTE_TYPE_ADMIN') return null
                      return (
                        <div
                          className={classNames(
                            'bg-tint-gray-100 flex items-center justify-between rounded p-2',
                            {
                              'bg-base-info-50': type === 'NOTE_TYPE_ADMIN',
                            },
                          )}
                          key={id}
                        >
                          <div className="w-full p-1 text-sm">
                            {noteEditing?.id === id ? (
                              <div className="flex gap-2">
                                <div className="flex w-full flex-col gap-1">
                                  <PixelFormItem
                                    label=""
                                    htmlFor="pixel-note-edit"
                                    valid={
                                      (noteEditing?.content || '').length <= 500
                                    }
                                    counter
                                    className="w-full"
                                  >
                                    <TextInput
                                      value={noteEditing?.content || ''}
                                      onChange={(e) =>
                                        setNoteEditing((prev) => ({
                                          ...prev,
                                          content: e.target.value,
                                        }))
                                      }
                                      maxLength={501}
                                    />
                                  </PixelFormItem>
                                  {isAdmin && (
                                    <Checkbox
                                      checked={
                                        noteEditing?.type === 'NOTE_TYPE_ADMIN'
                                      }
                                      label="Mark as admin note"
                                      onChange={(bool) => {
                                        if (bool) {
                                          setNoteEditing((prev) => ({
                                            ...prev,
                                            type: 'NOTE_TYPE_ADMIN',
                                          }))
                                        } else {
                                          setNoteEditing((prev) => ({
                                            ...prev,
                                            type: 'NOTE_TYPE_USER',
                                          }))
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                                <Button
                                  disabled={
                                    (noteEditing?.content || '')?.length >
                                      500 ||
                                    saving ||
                                    (noteEditing?.content || '')?.length < 1
                                  }
                                  onClick={() =>
                                    noteEditing && handleUpdateNote(noteEditing)
                                  }
                                  className="rounded-md !p-4"
                                  iconOnly={<Fa icon="save" size={1} />}
                                  kind="primary"
                                />
                                <Button
                                  onClick={() => setNoteEditing(undefined)}
                                  className="rounded-md !p-4"
                                  iconOnly={<Fa icon="xmark" size={1} />}
                                />
                              </div>
                            ) : (
                              <p>{content}</p>
                            )}
                            <p className="text-tint-gray-700 mt-1 text-xs">
                              {userName}{' '}
                              {(() => {
                                if (type === 'NOTE_TYPE_ADMIN')
                                  return (
                                    <span className="text-base-info font-bold">
                                      (Admin)
                                    </span>
                                  )
                                if (type === 'NOTE_TYPE_PUBLISHER')
                                  return (
                                    <span className="font-bold">
                                      (Publisher)
                                    </span>
                                  )
                                return null
                              })()}{' '}
                              {isAdmin ? ' - ' : ''}
                              {dayjs(createTime).format(
                                'MM/DD/YYYY, H:mm:ss A',
                              )}
                              {updateTime &&
                                !dayjs(updateTime).isSame(
                                  createTime,
                                  'millisecond',
                                ) &&
                                ` (edited ${dayjs(updateTime).format(
                                  'MM/DD/YYYY, H:mm:ss A',
                                )})`}
                            </p>
                          </div>
                          {noteEditing?.id !== id && (
                            <div className="flex gap-3">
                              {canEditNote && (
                                <button
                                  onClick={() => setNoteEditing(currentNote)}
                                  disabled={saving}
                                  className="pb-2 text-sm"
                                >
                                  Edit
                                </button>
                              )}
                              {canEditNote && (
                                <button
                                  onClick={() => id && handleDeleteNote(id)}
                                  disabled={saving}
                                  className="text-danger pb-2 text-sm"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    <span>No notes</span>
                  )}
                </div>
              </div>
            </div>

            {/* History */}
            {!isPartner && subscription.history && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] p-4 shadow-lg">
                <PixelSubscriptionHistory
                  type="subscription"
                  history={subscription.history}
                />
              </div>
            )}

            {/* Rulesets */}
            {!isPartner && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow">
                <div className="p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Rule Sets
                  </h2>
                  <RuleSets
                    parentRuleSet={parentRuleSet}
                    showLabel={false}
                    onDeleteRule={handleDeleteRule}
                    onDeleteRuleSet={handleDeleteRuleSet}
                    disabled={isReadOnly || subscription?.locked}
                    {...setters}
                  />
                </div>
              </div>
            )}

            {/* Geographic Filters */}
            {!isPartner && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow">
                <div className="p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Geographic Filters
                  </h2>
                  <PixelGeoLocationFilters
                    zipsFiltered={subscription?.zipsFiltered || []}
                    statesFiltered={subscription?.statesFiltered || []}
                    zipsIncluded={subscription?.zipsIncluded || []}
                    statesIncluded={subscription?.statesIncluded || []}
                    handleZipsFiltered={(zips) =>
                      setSubscription((prev) => ({
                        ...prev,
                        zipsFiltered: zips,
                      }))
                    }
                    handleStatesFiltered={(states) =>
                      setSubscription((prev) => ({
                        ...prev,
                        statesFiltered: states,
                      }))
                    }
                    handleZipsIncluded={(zips) =>
                      setSubscription((prev) => ({
                        ...prev,
                        zipsIncluded: zips,
                      }))
                    }
                    handleStatesIncluded={(states) =>
                      setSubscription((prev) => ({
                        ...prev,
                        statesIncluded: states,
                      }))
                    }
                    disabled={isReadOnly || subscription?.locked}
                  />
                </div>
              </div>
            )}

            {/* Processing Settings */}
            <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow">
              <div className="p-6">
                <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                  Processing Settings
                </h2>
                <div className="flex items-center">
                  <Toggle
                    isChecked={enabled}
                    onChange={(checked) => setEnabled(checked)}
                    size="s"
                    label="Enable Daily Processing"
                    isDisabled={
                      isReadOnly ||
                      subscription?.locked ||
                      (!isPartner && !enabled && !isValidRuleSet())
                    }
                  />
                </div>
                {!isPartner && !isValidRuleSet() && (
                  <p className="text-warning mt-2 text-sm">
                    Must have a valid rule set to enable daily processing.
                  </p>
                )}
                {enabled && (
                  <div className="px-4 py-2">
                    <PixelFormItem
                      htmlFor="subscription-ftp"
                      label={
                        <div className="flex items-center gap-1">
                          SFTP Path
                          {!isPartner &&
                            !isReadOnly &&
                            subscription.publisherId &&
                            currentOrg?.id &&
                            subscription.publisherId === currentOrg.id &&
                            (!isAdmin || groups?.includes('/Dev')) && ( // do not show to admins normally, but show if they are in the dev group for testing (Hari request)
                              <div onClick={(e) => e.stopPropagation()}>
                                <Link
                                  to={
                                    currentOrg?.id
                                      ? `/pixel-library/publisher/${currentOrg.id}/subscription/${subscriptionId}${query}`
                                      : `/pixel-library/partner-subscriptions${query}`
                                  }
                                  className="flex gap-1 text-blue-500 hover:text-blue-700"
                                  aria-describedby="ftp-path-link-description"
                                  aria-label="Navigate to publisher subscription view to edit SFTP path"
                                >
                                  <Fa
                                    icon="external-link"
                                    size={0.8}
                                    className="cursor-pointer"
                                    aria-label="External link icon"
                                  />
                                  <span id="ftp-path-link-description">
                                    Editable via publisher subscription view
                                  </span>
                                </Link>
                              </div>
                            )}
                        </div>
                      }
                    >
                      <TextInput
                        value={ftpPath}
                        onChange={(e) => setFtpPath(e.target.value)}
                        disabled={
                          subscription?.locked ||
                          isReadOnly ||
                          (!isPartner && !isAdmin)
                        }
                      />
                    </PixelFormItem>
                    {(!isPartner || isAdmin) && (
                      <div className="mt-2 flex">
                        <Toggle
                          isChecked={excludeDuplicates}
                          onChange={(checked) => {
                            setExcludeDuplicates(checked)
                            setExcludedTime(checked ? 86400 : undefined)
                          }}
                          size="s"
                          label="Exclude Duplicates"
                          isDisabled={isReadOnly || subscription?.locked}
                        />
                      </div>
                    )}
                    {excludeDuplicates && (
                      <select
                        value={excludedTime}
                        className="mt-2 rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-1.5 text-sm"
                        onChange={(e) => setExcludedTime(+e.target.value)}
                      >
                        {excludeTimeOptions.map((option, i) => (
                          <option
                            key={`${option.value}-${i}`}
                            value={option.value}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Administrative Settings */}
            {isAdmin && (
              <div className="mb-6 rounded-lg bg-[color:var(--base-bg)] shadow-lg">
                <div className="p-6">
                  <h2 className="mb-4 text-lg font-semibold text-[color:var(--base-text)]">
                    Administrative Settings
                  </h2>
                  <div className="space-y-6">
                    {!subscription.retargetingEnabled && (
                      <PixelFormItem
                        htmlFor="subscription-trial-period"
                        label="Trial Period"
                      >
                        <div className="flex w-max gap-2">
                          {/* @ts-ignore */}
                          <DatePicker
                            onChange={(start) => {
                              if (start) {
                                setTrialPeriodDates((prev) => {
                                  return { ...prev, start }
                                })
                              }
                            }}
                            minDate={new Date()}
                            selected={trialPeriodDates.start}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="Click to set start date"
                            popperClassName="start-date-popper"
                          />
                          {/* @ts-ignore */}
                          <DatePicker
                            onChange={(end) => {
                              if (end) {
                                setTrialPeriodDates((prev) => {
                                  return { ...prev, end }
                                })
                              }
                            }}
                            minDate={new Date()}
                            selected={trialPeriodDates.end}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="Click to set end date"
                            popperClassName="end-date-popper"
                          />
                        </div>
                        {trialPeriodDates.start > trialPeriodDates.end && (
                          <p className="text-warning mt-2 text-sm">
                            Start date cannot be after the end date.
                          </p>
                        )}
                      </PixelFormItem>
                    )}
                    <div className="PixelSubscriptionsPage__admin-toggles flex items-center gap-6">
                      <Toggle
                        isChecked={hidden}
                        onChange={(checked) => setHidden(checked)}
                        size="s"
                        label="Hide Subscription"
                        isDisabled={isReadOnly}
                      />
                      <Toggle
                        isChecked={locked}
                        onChange={(checked) => setLocked(checked)}
                        size="s"
                        label="Lock Subscription"
                        isDisabled={isReadOnly}
                      />
                      {!subscription.retargetingEnabled && (
                        <Toggle
                          isChecked={autoEnableAfterTrial}
                          onChange={(checked) =>
                            setAutoEnableAfterTrial(checked)
                          }
                          size="s"
                          label="Automatically enable daily processing after trial"
                          isDisabled={isReadOnly}
                        />
                      )}
                      {!subscription.retargetingEnabled && (
                        <Toggle
                          isChecked={showMatchCap}
                          onChange={(checked) => {
                            setShowMatchCap(checked)
                            setMatchCap(0)
                          }}
                          size="s"
                          label="Match Cap"
                          isDisabled={isReadOnly}
                        />
                      )}
                      {!subscription.retargetingEnabled && (
                        <Toggle
                          isChecked={sendToFileServer}
                          onChange={(checked) => setSendToFileServer(checked)}
                          size="s"
                          label="Send to File Server"
                          isDisabled={isReadOnly || subscription?.locked}
                        />
                      )}
                    </div>
                    {showMatchCap && (
                      <div>
                        <div className="w-max">
                          <label className="FormItem__label mb-1 block text-sm">
                            Match Cap Value
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={matchCap}
                            onChange={(e) => setMatchCap(+e.target.value)}
                            className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-2 text-[color:var(--base-text)]"
                            disabled={isReadOnly}
                          />
                        </div>
                        {matchCap < 1 && (
                          <p className="text-warning mt-2 text-sm">
                            Match cap must be greater than 0.
                          </p>
                        )}
                      </div>
                    )}
                    <div>
                      <label className="FormItem__label mb-1 block text-sm">
                        Publisher
                      </label>
                      <select
                        value={selectedPublisher?.id}
                        onChange={(e) => {
                          const pub = publishers.find(
                            (publisher) => publisher.id === e.target.value,
                          )
                          setSelectedPublisher(pub)
                          if (!pub) {
                            setSendToFileServer(false)
                          } else {
                            setSendToFileServer(true)
                          }
                        }}
                        className="w-full rounded-md border border-[color:var(--color-grey-200)] bg-[color:var(--base-bg)] px-3 py-1.5 text-sm text-[color:var(--base-text)] transition-colors hover:border-[color:var(--color-grey-300)]"
                        disabled={isReadOnly}
                      >
                        <option value="">Select Publisher</option>
                        {publishers.map((publisher) => (
                          <option value={publisher.id} key={publisher.id}>
                            {publisher.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Button
              onClick={handleUpdateSubscription}
              className="btn-primary w-full"
              disabled={
                isReadOnly ||
                saving ||
                trialPeriodDates.start > trialPeriodDates.end ||
                (showMatchCap && matchCap < 1) ||
                (enabled && !isValidRuleSet())
              }
              kind="primary"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
      {supportModal && (
        <SupportRequestModal handleClose={() => setSupportModal(false)} />
      )}
    </div>
  )
}
